import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import BookingDocuments from '@/components/BookingDocuments';
import {
  FileText,
  Mail,
  Clock,
  CheckCircle2,
  Hourglass,
  Download,
  ChevronRight,
  AlertCircle,
  Calendar as CalIcon
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const AEST_TZ = 'Australia/Brisbane';

const STATUS_FILTERS = [
  { key: 'all', label: 'All reports' },
  { key: 'completed', label: 'Completed' },
  { key: 'pending', label: 'In progress' }
];

const STAGE_LABEL = {
  intake_submitted: 'Initial Enquiry Submitted',
  deposit_paid: 'Deposit Paid',
  report_issued: 'Report Issued',
  next_stage_recommended: 'Next Stage Recommended',
  next_stage_confirmed: 'Next Stage Confirmed',
  next_stage_paid: 'Next Stage Paid',
  completed: 'Workflow Complete'
};

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return '—';
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(i === 0 ? 0 : 1)} ${sizes[i]}`;
}

function addBusinessDays(baseIso, days) {
  if (!baseIso) return null;
  const d = new Date(baseIso);
  let added = 0;
  while (added < days) {
    d.setUTCDate(d.getUTCDate() + 1);
    const wd = d.getUTCDay();
    if (wd !== 0 && wd !== 6) added += 1;
  }
  return d;
}

function formatAEST(iso, opts = {}) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-AU', {
    timeZone: AEST_TZ,
    ...opts
  });
}

export default function Reports() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [downloadingId, setDownloadingId] = useState(null);

  const fetchBookings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(res.data);
    } catch (err) {
      console.error('Reports fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const filtered = useMemo(() => {
    if (filter === 'all') return bookings;
    if (filter === 'completed') return bookings.filter((b) => !!b.report_pdf_path);
    return bookings.filter((b) => !b.report_pdf_path);
  }, [bookings, filter]);

  const counts = useMemo(() => {
    const completed = bookings.filter((b) => !!b.report_pdf_path).length;
    return {
      all: bookings.length,
      completed,
      pending: bookings.length - completed
    };
  }, [bookings]);

  const handleDownload = async (b) => {
    if (!b.report_pdf_path) return;
    setDownloadingId(b.id);
    try {
      const res = await axios.get(`${API}/bookings/${b.id}/report-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = b.report_pdf_filename || `report-${b.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Download failed.';
      // eslint-disable-next-line no-alert
      alert(detail);
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
            <p className="mt-4 text-sm text-slate-600">Loading reports…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      <div
        data-testid="reports-page"
        className="max-w-7xl mx-auto px-6 py-8 mt-16"
      >
        {/* Header */}
        <div className="mb-6">
          <h1
            className="text-4xl font-bold tracking-tight text-black mb-1"
            style={{ fontFamily: 'Outfit' }}
          >
            Reports &amp; Status
          </h1>
          <p className="text-slate-600 max-w-3xl">
            Status of every enquiry — <strong>when</strong> the All Build Consult team will
            respond, <strong>how</strong> the report reaches you (your registered email), and{' '}
            <strong>what</strong> has been delivered (file type, size, count).
          </p>
        </div>

        {/* Status filter pills + counters */}
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`reports-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 rounded-sm text-sm font-medium border transition-all inline-flex items-center gap-2 ${
                filter === f.key
                  ? 'bg-black text-white border-black'
                  : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400'
              }`}
            >
              {f.label}
              <span
                className={`px-1.5 py-0.5 text-[10px] font-bold rounded-sm ${
                  filter === f.key ? 'bg-white text-black' : 'bg-slate-100 text-slate-700'
                }`}
              >
                {counts[f.key]}
              </span>
            </button>
          ))}
        </div>

        {/* Empty state */}
        {filtered.length === 0 ? (
          <div className="grid-border bg-white p-12 text-center">
            <AlertCircle className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-600 mb-4">
              {bookings.length === 0
                ? 'No enquiries yet. Start a pre-assessment to see your report status here.'
                : 'No reports match this filter.'}
            </p>
            {bookings.length === 0 && (
              <button
                onClick={() => navigate('/bookings')}
                className="px-5 py-2 bg-black text-white text-sm font-medium rounded-sm hover:bg-slate-800"
              >
                Go to Bookings
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((b) => {
              const hasReport = !!b.report_pdf_path;
              const isPaid = b.payment_status === 'paid';
              const isTradeWorks = (b.service_type || '').toLowerCase().includes('trade works');
              const inspectionDate = b.preferred_slot ? new Date(b.preferred_slot) : null;
              const reportEta = inspectionDate
                ? addBusinessDays(inspectionDate.toISOString(), 5)
                : null;
              const recipientEmail = b.billing_email || user?.email || '—';

              return (
                <article
                  key={b.id}
                  data-testid="reports-row"
                  className="grid-border bg-white overflow-hidden"
                >
                  {/* Row header */}
                  <header className="p-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0 flex-wrap">
                      <FileText
                        className={`w-5 h-5 flex-shrink-0 ${hasReport ? 'text-emerald-700' : 'text-slate-400'}`}
                      />
                      <h2
                        className="text-base font-semibold text-black"
                        style={{ fontFamily: 'Outfit' }}
                      >
                        {b.service_type}
                      </h2>
                      {isTradeWorks && (
                        <span className="px-1.5 py-0.5 bg-red-600 text-white text-[9px] font-bold uppercase tracking-wider rounded-sm">
                          Coming Soon
                        </span>
                      )}
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-[10px] uppercase font-semibold tracking-wider rounded-sm">
                        {STAGE_LABEL[b.stage] || b.stage}
                      </span>
                      {hasReport ? (
                        <span
                          data-testid="reports-status-completed"
                          className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] uppercase font-bold tracking-wider rounded-sm inline-flex items-center gap-1"
                        >
                          <CheckCircle2 className="w-3 h-3" />
                          Completed
                        </span>
                      ) : (
                        <span
                          data-testid="reports-status-pending"
                          className="px-2 py-0.5 bg-amber-100 text-amber-800 text-[10px] uppercase font-bold tracking-wider rounded-sm inline-flex items-center gap-1"
                        >
                          <Hourglass className="w-3 h-3" />
                          In Progress
                        </span>
                      )}
                    </div>
                    {hasReport ? (
                      <button
                        data-testid={`reports-download-${b.id}`}
                        disabled={!isPaid || b.download_locked || downloadingId === b.id}
                        onClick={() => handleDownload(b)}
                        title={
                          !isPaid
                            ? 'Payment required before download'
                            : b.download_locked
                              ? 'Download currently locked by admin'
                              : 'Download report PDF'
                        }
                        className="px-3 py-1.5 bg-black text-white text-xs font-medium rounded-sm hover:bg-slate-800 transition-all inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Download className="w-3.5 h-3.5" />
                        {downloadingId === b.id ? 'Downloading…' : 'Download'}
                      </button>
                    ) : (
                      <button
                        onClick={() => navigate('/payments')}
                        className="px-3 py-1.5 bg-white text-slate-700 text-xs font-medium border border-slate-300 rounded-sm hover:bg-slate-50 inline-flex items-center gap-1"
                      >
                        View progress
                        <ChevronRight className="w-3 h-3" />
                      </button>
                    )}
                  </header>

                  {/* When / How / What — 3-column detail register */}
                  <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-100">
                    {/* WHEN */}
                    <section data-testid="reports-when" className="p-4">
                      <div className="text-[10px] uppercase tracking-wide font-bold text-slate-500 mb-2 inline-flex items-center gap-1">
                        <CalIcon className="w-3 h-3" />
                        When
                      </div>
                      <dl className="text-sm space-y-1.5">
                        <div className="flex justify-between gap-2">
                          <dt className="text-slate-500">Booked</dt>
                          <dd className="text-black font-medium">
                            {formatAEST(b.created_at, {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </dd>
                        </div>
                        <div className="flex justify-between gap-2">
                          <dt className="text-slate-500">Initial review</dt>
                          <dd className="text-black font-medium">
                            {inspectionDate
                              ? formatAEST(inspectionDate.toISOString(), {
                                  day: 'numeric',
                                  month: 'short',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  hour12: true
                                }) + ' AEST'
                              : 'Pending'}
                          </dd>
                        </div>
                        {hasReport ? (
                          <div className="flex justify-between gap-2">
                            <dt className="text-emerald-700 font-medium">Delivered</dt>
                            <dd className="text-emerald-800 font-semibold inline-flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" />
                              {b.report_pdf_uploaded_at
                                ? formatAEST(b.report_pdf_uploaded_at, {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric'
                                  })
                                : 'Issued'}
                            </dd>
                          </div>
                        ) : (
                          <div className="flex justify-between gap-2">
                            <dt className="text-amber-700 font-medium">Report ETA</dt>
                            <dd className="text-amber-800 font-semibold inline-flex items-center gap-1">
                              <Hourglass className="w-3 h-3" />
                              {reportEta
                                ? `By ${formatAEST(reportEta.toISOString(), {
                                    day: 'numeric',
                                    month: 'short'
                                  })}`
                                : '3–5 business days'}
                            </dd>
                          </div>
                        )}
                      </dl>
                    </section>

                    {/* HOW */}
                    <section data-testid="reports-how" className="p-4">
                      <div className="text-[10px] uppercase tracking-wide font-bold text-slate-500 mb-2 inline-flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        How
                      </div>
                      <dl className="text-sm space-y-1.5">
                        <div>
                          <dt className="text-slate-500 text-xs">Sent to</dt>
                          <dd
                            className="text-black font-medium truncate"
                            title={recipientEmail}
                          >
                            {recipientEmail}
                          </dd>
                        </div>
                        <div className="flex justify-between gap-2">
                          <dt className="text-slate-500">Channel</dt>
                          <dd className="text-black font-medium">
                            Email &amp; in-app
                          </dd>
                        </div>
                        <div className="flex justify-between gap-2">
                          <dt className="text-slate-500">Payment</dt>
                          <dd>
                            {isPaid ? (
                              <span className="text-green-700 font-semibold inline-flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" />
                                Paid
                              </span>
                            ) : (
                              <span className="text-amber-700 font-semibold inline-flex items-center gap-1">
                                <Hourglass className="w-3 h-3" />
                                Outstanding
                              </span>
                            )}
                          </dd>
                        </div>
                      </dl>
                    </section>

                    {/* WHAT */}
                    <section data-testid="reports-what" className="p-4">
                      <div className="text-[10px] uppercase tracking-wide font-bold text-slate-500 mb-2 inline-flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        What
                      </div>
                      <dl className="text-sm space-y-1.5">
                        {hasReport ? (
                          <>
                            <div>
                              <dt className="text-slate-500 text-xs">Filename</dt>
                              <dd
                                className="text-black font-medium truncate"
                                title={b.report_pdf_filename || 'report.pdf'}
                              >
                                {b.report_pdf_filename || 'report.pdf'}
                              </dd>
                            </div>
                            <div className="flex justify-between gap-2">
                              <dt className="text-slate-500">File type</dt>
                              <dd className="text-black font-medium">PDF document</dd>
                            </div>
                            <div className="flex justify-between gap-2">
                              <dt className="text-slate-500">Size</dt>
                              <dd className="text-black font-medium">
                                {formatBytes(b.report_pdf_size_bytes)}
                              </dd>
                            </div>
                            <div className="flex justify-between gap-2">
                              <dt className="text-slate-500">Documents</dt>
                              <dd className="text-black font-medium">1 file</dd>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="flex justify-between gap-2">
                              <dt className="text-slate-500">File type</dt>
                              <dd className="text-slate-400">PDF (pending)</dd>
                            </div>
                            <div className="flex justify-between gap-2">
                              <dt className="text-slate-500">Size</dt>
                              <dd className="text-slate-400">—</dd>
                            </div>
                            <div className="flex justify-between gap-2">
                              <dt className="text-slate-500">Documents</dt>
                              <dd className="text-slate-400">0 files</dd>
                            </div>
                            <p className="text-xs text-amber-700 bg-amber-50 border-l-2 border-amber-500 p-2 rounded-sm mt-2">
                              Report appears here once the All Build Consult team issues it
                              after your initial review.
                            </p>
                          </>
                        )}
                      </dl>
                    </section>
                  </div>

                  {/* Footer — quick link */}
                  <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 flex items-center justify-between gap-2 text-xs text-slate-600 flex-wrap">
                    <div className="inline-flex items-center gap-2 min-w-0">
                      <Clock className="w-3 h-3" />
                      <span className="truncate">{b.property_address || 'No property address'}</span>
                    </div>
                    <button
                      onClick={() => navigate('/bookings')}
                      className="text-blue-700 hover:text-blue-900 inline-flex items-center gap-1"
                    >
                      Open booking
                      <ChevronRight className="w-3 h-3" />
                    </button>
                  </div>

                  {/* Documents (multi-doc review with tick-to-acknowledge) */}
                  <BookingDocuments
                    bookingId={b.id}
                    token={token}
                    role={user?.role || 'client'}
                  />
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
